<template>
  <div class="license-content">
    <table v-if="sortedLicenses && sortedLicenses.length > 0" class="license-table">
      <thead>
        <tr>
          <th class="name">License Name</th>
          <th class="expiry-date">Expiry Date</th>
          <th class="status">Status</th>
          <th class="start-date">Start Date</th>
          <!-- <th class="screen">Screen</th> -->
          <th class="creator">Creator</th>
          <th class="delete"></th>
        </tr>
      </thead>
      <tbody class="scrollbar">
        <tr
          v-for="license in sortedLicenses"
          :key="license.id"
          :class="{
            selected: isSelected(license.id) && !renewMode,
            'near-expiration': isNearExpiration(license.expired_at),
            expired: checkExpiration(license.expired_at) === 'expired',
            'renew-selected': isSelected(license.id) && renewMode,
          }"
          @click="setSelectedLicense(license)"
        >
          <td class="name">
            {{ license.name | alwaysShowStr }}
          </td>

          <td
            :class="{
              'near-expiration-text': checkExpiration(license.expired_at),
            }"
          >
            {{ formattedExpiryDate(license.expired_at, 'DD/MM/YYYY') }}
          </td>

          <td
            :class="{
              'text-green': license.status === 'active',
              'text-expired': license.status === 'expired',
              'text-danger': license.status === 'removed',
            }"
          >
            {{ getStatusLabel(license.status) }}
          </td>

          <td>
            {{ formattedDate(license.started_at, 'DD/MM/YYYY') }}
          </td>

          <td v-if="license.created_by !== null">{{ license.created_by }}</td>
          <td v-else>N/A</td>

          <!-- <td v-if="license.screen_details === null">N/A</td>
          <td v-else>{{ license.screen_details.name }}</td> -->

          <td v-if="isAdmin" style="width: 64px" class="delete-option">
            <div class="dropdown">
              <i
                class="material-icons-outlined align-middle more-buttons dropdown-toggle"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                >more_vert</i
              >
              <div class="dropdown-menu dropdown-menu-right" v-show="license.status !== 'removed'">
                <button
                  v-if="isAdmin"
                  type="button"
                  class="dropdown-item"
                  @click="openDeleteLicenseModal(license)"
                >
                  <i class="material-icons-outlined align-middle">delete</i>
                  Delete
                </button>
              </div>
            </div>
          </td>
          <td v-else style="width: 0px"></td>
        </tr>
      </tbody>
    </table>

    <div v-else class="no-data">
      License does not exist, use the add button to create a new one.
    </div>

    <DeleteLicenseModal
      @closeModal="showDeleteLicenseModal = false"
      :license="selectedLicense"
      v-if="showDeleteLicenseModal"
      @deleted="$emit('onDelete')"
    />
  </div>
</template>

<script>
  import moment from 'moment';
  import { LICENSE_STATUSES } from '@/config/constants';
  import DeleteLicenseModal from '@/components/admin/OrganisationDetails/Licenses/DeleteLicenseModal.vue';

  export default {
    name: 'LicensesList',

    components: {
      DeleteLicenseModal,
    },

    props: {
      licenses: {
        type: Array,
        default: () => [],
      },

      renewMode: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        showDeleteLicenseModal: false,
        selectedLicense: null,
        selectedLicenses: [],
      };
    },

    watch: {
      renewMode(value) {
        this.selectedLicense = null;
        this.selectedLicenses = [];
      },
    },

    computed: {
      isAdmin() {
        return this.$store.getters.isAdministrator;
      },
      sortedLicenses() {
        return this.licenses.slice().sort((a, b) => {
          return moment(a.expired_at) - moment(b.expired_at);
        });
      },
    },

    methods: {
      checkDate(date) {
        return moment(date).isBefore();
      },

      setSelectedLicense(license = null) {
        if (!this.renewMode) {
          this.selectedLicense = license;
          this.$emit('licenseSelected', license);
          return;
        }

        const removeLicense = this.selectedLicenses.some(
          (selectedLicense) => selectedLicense.id === license.id,
        );

        if (removeLicense) {
          this.selectedLicenses = this.selectedLicenses.filter(
            (selectedLicense) => selectedLicense.id !== license.id,
          );
        } else {
          this.selectedLicenses.push(license);
        }

        this.$emit('licenseSelected', this.selectedLicenses);
      },

      openDeleteLicenseModal(license) {
        this.setSelectedLicense(license);
        this.showDeleteLicenseModal = true;
      },

      getStatusLabel(statusKey) {
        return LICENSE_STATUSES[statusKey];
      },

      isSelected(licenseId) {
        if (this.renewMode) {
          return this.selectedLicenses.some((l) => l.id === licenseId);
        }

        return this.selectedLicense?.id === licenseId;
      },

      expirationDateColor(date) {
        const newDate = moment(date, 'DD/MM/YYYY').toDate();
        const colorClass = moment(newDate).isBefore() ? 'text-expired' : '';

        // TODO: orange condition

        return colorClass;
      },

      formattedDate(date, format) {
        return moment(date).format(format);
      },

      expiredDate(license) {
        const startDate = moment(license.started_at);
        return startDate.add(license.expired_time, 'days');
      },

      checkExpiration(expired_at) {
        const today = moment().startOf('day');
        const expirationDate = moment(expired_at).startOf('day');

        if (expirationDate.isSame(today)) {
          return 'expires-today';
        }

        if (expirationDate.isBefore(today)) {
          return 'expired';
        }

        const diffInDays = expirationDate.diff(today, 'days');
        return diffInDays <= 90 ? diffInDays : null;
      },

      isNearExpiration(expired_at) {
        const expirationStatus = this.checkExpiration(expired_at);
        return expirationStatus && expirationStatus !== 'expired';
      },

      formattedExpiryDate(expired_at, format) {
        const daysUntilExpiration = this.checkExpiration(expired_at);

        if (daysUntilExpiration === 'expires-today') {
          return 'Today';
        } else if (daysUntilExpiration === 'expired') {
          return 'Expired';
        } else if (daysUntilExpiration !== null) {
          return `in ${daysUntilExpiration} days`;
        } else {
          return moment(expired_at).format(format);
        }
      },
    },
  };
</script>
<style scoped lang="scss">
  .template-container {
    display: flex;
    width: 100%;
    height: 70vh;
    padding-bottom: 30px;
  }

  .license-content {
    height: 100%;
  }

  table {
    background-color: $backgroundGrey;
    width: 100%;
    font-size: 14px;
    border: none;
    border-spacing: 0 8px;
    border-collapse: separate;
    table-layout: fixed;
    overflow-y: auto;
  }

  thead {
    background-color: $backgroundGrey;
    height: 50px;
    position: sticky;
    top: 0;
    z-index: 2;

    th {
      padding-left: 15px;
    }

    th.name {
      width: 33%;
    }

    th.expiry-date {
      width: 12%;
    }

    th.status {
      width: 10%;
    }

    th.start-date {
      width: 12%;
    }

    th.screen {
      width: 10%;
    }

    th.creator {
      width: 20%;
    }

    th.delete {
      width: 3%;
    }

    th {
      border-top: 2px solid #f7f8f7;
      border-bottom: 2px solid #f7f8f7;
      overflow-wrap: break-word;

      &:first-child {
        border-left: 2px solid #f7f8f7;
        font-weight: bold;
        width: 20%;
      }

      &:last-child {
        border-right: 2px solid #f7f8f7;
      }
    }
  }

  tbody {
    // max-height: 50vh;
    overflow-y: auto;

    tr {
      background-color: #fff;
      cursor: pointer;
      font-size: 14px;
      table-layout: fixed;

      td {
        padding: 15px;

        .dropdown {
          display: flex;
          justify-content: flex-end;
        }

        border-top: 2px solid transparent;
        border-bottom: 2px solid transparent;
        overflow-wrap: break-word;

        &:first-child {
          border-left: 2px solid transparent;
          font-weight: bold;
          width: 20%;
        }

        &:last-child {
          border-right: 2px solid transparent;
        }
      }

      &:hover,
      &.selected,
      &.renew-selected {
        td {
          border-top: 2px solid $primaryRed;
          border-bottom: 2px solid $primaryRed;

          &:first-child {
            border-left: 2px solid $primaryRed;
          }

          &:last-child {
            border-right: 2px solid $primaryRed;
          }
        }
      }
    }
  }

  tr.near-expiration {
    background-color: #ffdde6;
    border-color: #ffdde6;
  }
  .near-expiration-text {
    color: $primaryRed;
  }

  // td.near-expiration {
  //   font-weight: bold;
  // }

  tr.expired {
    background-color: #f5f5f5;

    td {
      opacity: 0.5;
    }
  }

  .sidebar-container {
    display: none;
    height: 100%;

    :active {
      display: block;
    }
  }

  .container-header {
    display: grid;
    grid-template-columns: auto 160px 160px;
    margin: 20px 10px;
    height: 44px;

    .button {
      border: none;
      background-color: transparent;
      font-size: 16px;
      font-weight: bold;
      display: flex;
      align-items: center;
      height: 40px;
      justify-content: space-evenly;
    }
  }

  .add-button {
    color: $primaryRed;
  }

  .renew-button {
    color: $secondaryText;
  }
</style>
